import { apiClient } from './api';
import type { CartItem, SelectedOption, MenuItem } from '@/types';

export interface BackendCartItem {
  id: string;
  productId: string;
  productName: string;
  productImage: string | null;
  optionIds: string[];
  optionNames: string[];
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  specialInstructions?: string | null;
  removedIngredientIds?: string[];
  removedIngredientNames?: string[];
}

export interface BackendCart {
  userId: string;
  items: BackendCartItem[];
  cartTotal: number;
  restaurantId: string | null;
  serviceZoneId: string | null;
  minimumOrderAmount: number | null;
  deliveryFee: number | null;
  meetsMinimumOrder: boolean;
  lastUpdated: string;
  ttl: number;
}

export interface AddCartItemDto {
  productId: string;
  optionIds?: string[];
  removedIngredientIds?: string[];
  quantity: number;
  specialInstructions?: string;
}

export interface UpdateCartItemDto {
  quantity: number;
}

export interface SetServiceZoneDto {
  serviceZoneId: string;
}

export const cartService = {
  async getCart(): Promise<BackendCart> {
    const response = await apiClient.get<BackendCart>('/cart');
    return response.data.data;
  },

  async addItem(item: CartItem): Promise<BackendCart> {
    const optionIds = item.selectedOptions.map(opt => opt.optionId);
    
    const dto: AddCartItemDto = {
      productId: item.menuItemId,
      optionIds: optionIds.length > 0 ? optionIds : undefined,
      removedIngredientIds: item.removedIngredientIds?.length ? item.removedIngredientIds : undefined,
      quantity: item.quantity,
      specialInstructions: item.specialInstructions,
    };

    const response = await apiClient.post<BackendCart>('/cart/items', dto);
    return response.data.data;
  },

  async updateItem(itemId: string, quantity: number): Promise<BackendCart> {
    const dto: UpdateCartItemDto = { quantity };
    const response = await apiClient.put<BackendCart>(`/cart/items/${itemId}`, dto);
    return response.data.data;
  },

  /**
   * Remove item from cart
   * 
   * NOTE: Backend uses cart item ID (from backend), not frontend itemKey
   */
  async removeItem(itemId: string): Promise<BackendCart> {
    const response = await apiClient.delete<BackendCart>(`/cart/items/${itemId}`);
    return response.data.data;
  },

  /**
   * Clear cart
   */
  async clearCart(): Promise<BackendCart> {
    const response = await apiClient.delete<BackendCart>('/cart');
    return response.data.data;
  },

  /**
   * Set service zone for cart
   */
  async setServiceZone(serviceZoneId: string): Promise<BackendCart> {
    const dto: SetServiceZoneDto = { serviceZoneId };
    const response = await apiClient.post<BackendCart>('/cart/service-zone', dto);
    return response.data.data;
  },
};

/**
 * Type Mapping Utilities
 * 
 * Maps between backend cart format and frontend cart format
 * 
 * CRITICAL LIMITATION:
 * - Backend cart items don't store optionGroupId
 * - Frontend CartItem format requires SelectedOption[] with optionGroupId
 * - To properly map, we need menuItem data to lookup optionGroupId from optionId
 * - For now, we create SelectedOption[] with optionId only (optionGroupId will be empty string)
 * 
 * TODO: Consider updating backend cart format to include optionGroupId
 * OR: Update frontend CartItem format to match backend format
 */
export const cartTypeMappers = {
  /**
   * Convert backend cart item to frontend cart item
   * 
   * NOTE: optionGroupId is missing in backend format
   * This mapping creates SelectedOption[] with optionId only
   * optionGroupId is set to empty string (requires menuItem data to properly map)
   */
  backendItemToFrontendItem(
    backendItem: BackendCartItem,
    menuItem?: MenuItem,
  ): CartItem {
    // Map optionIds to SelectedOption[]
    // If menuItem is provided, we can properly map optionGroupId
    const selectedOptions: SelectedOption[] = backendItem.optionIds.map(optionId => {
      // Try to find optionGroupId from menuItem
      let optionGroupId = '';
      if (menuItem?.optionGroups) {
        for (const group of menuItem.optionGroups) {
          if (group.options.some(opt => opt.id === optionId)) {
            optionGroupId = group.id;
            break;
          }
        }
      }
      return {
        optionGroupId,
        optionId,
      };
    });

    return {
      menuItemId: backendItem.productId,
      quantity: backendItem.quantity,
      selectedOptions,
      backendItemId: backendItem.id, // Store backend item ID for update/remove operations
      itemTotal: backendItem.unitPrice || 0,
      specialInstructions: backendItem.specialInstructions || undefined,
      removedIngredientIds: backendItem.removedIngredientIds || [],
      removedIngredientNames: backendItem.removedIngredientNames || [],
    };
  },

  /**
   * Convert backend cart to frontend cart format
   * 
   * NOTE: restaurantId and restaurant data must be set separately
   * This mapping only converts items and totals
   */
  backendCartToFrontendCart(
    backendCart: BackendCart,
    menuItemsMap?: Map<string, MenuItem>, // productId -> MenuItem lookup
  ): {
    items: CartItem[];
    subtotal: number; // in TRY
    serviceZoneId: string | null;
  } {
    const items: CartItem[] = backendCart.items.map(item => {
      const menuItem = menuItemsMap?.get(item.productId);
      return this.backendItemToFrontendItem(item, menuItem);
    });

    return {
      items,
      subtotal: backendCart.cartTotal || 0,
      serviceZoneId: backendCart.serviceZoneId,
    };
  },
};
